// src/pages/CompanySettings.js
import React, {
  useEffect,
  useMemo,
  useRef,
} from "react";

import {
  useLocation,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import {
  LuBuilding2,
  LuCoins,
  LuFileText,
  LuGlobe,
  LuHandshake,
  LuKey,
  LuMail,
  LuMapPin,
  LuPencil,
  LuPhone,
  LuShieldCheck,
  LuTruck,
  LuUser,
} from "react-icons/lu";

import api from "../axiosConfig";

import {
  getSocket,
} from "../lib/socket";

import CompanyProfile from "./CompanyProfile";
import OperationDetails from "./OperationDetails";
import ContactInformation from "./ContactInformation";
import FinancialInformation from "./FinancialInformation";
import PaymentTerms from "./PaymentTerms";
import TaxInformation from "./TaxInformation";

import {
  useCompanySettingsData,
} from "../hooks/useCompanySettingsData";

import "../styles/CompanySettings.css";

const EMPTY_OVERVIEW = {
  name: "",
  address: "",
  city: "",
  country: "",
  email: "",
  phoneNumber: "",
  photo: null,
  taxId: "",
  currency: "",
  paymentNetDays: null,
  driversEmployed: null,
};

const firstDefined = (
  ...values
) => {
  for (
    const value of values
  ) {
    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== ""
    ) {
      return value;
    }
  }

  return "";
};

const toNullableValue = (
  value
) => {
  if (
    value === undefined ||
    value === null ||
    String(value).trim() === ""
  ) {
    return null;
  }

  return value;
};

const absoluteFromApiBase = (
  path
) => {
  if (!path) {
    return null;
  }

  if (
    /^https?:\/\//i.test(
      path
    )
  ) {
    return path;
  }

  const base = (
    api.defaults?.baseURL || ""
  ).replace(
    /\/$/,
    ""
  );

  return base
    ? `${base}${
        path.startsWith("/")
          ? ""
          : "/"
      }${path}`
    : path;
};

const companyPhotoFromDetails =
  (
    details
  ) => {
    const raw =
      firstDefined(
        details?.photoCompany,
        details?.photo_company
      );

    if (!raw) {
      return null;
    }

    /*
     * Backend photoCompany is normally a presigned
     * S3/Hetzner URL. Keep it completely untouched.
     */
    if (
      /^https?:\/\//i.test(
        raw
      )
    ) {
      return raw;
    }

    const absolute =
      absoluteFromApiBase(
        raw
      );

    const revision =
      firstDefined(
        details?.updatedAt,
        details?.updated_at
      ) ||
      Date.now();

    const separator =
      absolute.includes("?")
        ? "&"
        : "?";

    return `${absolute}${separator}v=${encodeURIComponent(
      revision
    )}`;
  };

const getInitials = (
  name
) => {
  const words =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

  if (!words.length) {
    return "LE";
  }

  if (
    words.length === 1
  ) {
    return words[0]
      .slice(0, 2)
      .toUpperCase();
  }

  return `${words[0][0]}${words[1][0]}`
    .toUpperCase();
};

const hasOverviewValue = (
  value
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return false;
  }

  if (
    typeof value ===
    "number"
  ) {
    return true;
  }

  return (
    String(value).trim() !==
    ""
  );
};

export default function CompanySettings() {
  const { t } =
    useTranslation(
      "company_settings"
    );

  const location =
    useLocation();

  const companyProfileRef =
    useRef(null);

  const wsRefreshTimerRef =
    useRef(null);

  const {
    data,
    refresh,
  } =
    useCompanySettingsData();

  const companyDetailsId =
    data?.details?.id ??
    null;

  const overview =
    useMemo(() => {
      if (!data) {
        return EMPTY_OVERVIEW;
      }

      const details =
        data.details || {};

      const operations =
        data.operations || {};

      const financial =
        data.financial || {};

      const tax =
        data.tax || {};

      const paymentNetDays =
        firstDefined(
          details.payment_net_days,
          details.paymentNetDays
        );

      const driversEmployed =
        firstDefined(
          operations.drivers_employed,
          operations.driversEmployed,
          details.drivers_employed,
          details.driversEmployed
        );

      return {
        name:
          firstDefined(
            details.name,
            details.company_name
          ),

        address:
          firstDefined(
            details.address
          ),

        city:
          firstDefined(
            details.city
          ),

        country:
          firstDefined(
            details.country
          ),

        email:
          firstDefined(
            details.companyEmail,
            details.company_email,
            details.email
          ),

        phoneNumber:
          firstDefined(
            details.companyPhoneNumber,
            details.company_phone_number,
            details.phoneNumber,
            details.phone_number,
            details.phone,
            details.company_phone
          ),

        photo:
          companyPhotoFromDetails(
            details
          ),

        taxId:
          firstDefined(
            tax.vat_tax_id,
            tax.vatTaxId,
            tax.tax_id,
            tax.taxId,
            details.vat_tax_id,
            details.vatTaxId,
            details.tax_id,
            details.taxId
          ),

        currency:
          firstDefined(
            financial.currency,
            details.currency
          ),

        paymentNetDays:
          toNullableValue(
            paymentNetDays
          ),

        driversEmployed:
          toNullableValue(
            driversEmployed
          ),
      };
    }, [
      data,
    ]);

    useEffect(() => {
      const socket =
        getSocket();
  
      const subscribe =
        () => {
          socket.emit(
            "subscribe:companyprofile"
          );
  
          if (
            companyDetailsId
          ) {
            socket.emit(
              "subscribe:companydetails",
              {
                id:
                  companyDetailsId,
              }
            );
          }
        };
  
      const scheduleRefresh =
        () => {
          if (
            wsRefreshTimerRef.current
          ) {
            window.clearTimeout(
              wsRefreshTimerRef.current
            );
          }
  
          wsRefreshTimerRef.current =
            window.setTimeout(
              () => {
                wsRefreshTimerRef.current =
                  null;
  
                Promise.resolve(
                  refresh()
                ).catch(
                  (
                    error
                  ) => {
                    console.error(
                      "[CompanySettings][ws-refresh] error",
                      error
                    );
                  }
                );
              },
              80
            );
        };
  
      subscribe();
  
      socket.on(
        "connect",
        subscribe
      );
  
      socket.on(
        "reconnect",
        subscribe
      );
  
      socket.on(
        "company:updated",
        scheduleRefresh
      );
  
      socket.on(
        "company:created",
        scheduleRefresh
      );
  
      socket.on(
        "companydetails:updated",
        scheduleRefresh
      );
  
      socket.on(
        "companydetails:created",
        scheduleRefresh
      );
  
      socket.on(
        "companydetails:deleted",
        scheduleRefresh
      );
  
      socket.on(
        "companyprofile:changed",
        scheduleRefresh
      );
  
      return () => {
        if (
          wsRefreshTimerRef.current
        ) {
          window.clearTimeout(
            wsRefreshTimerRef.current
          );
  
          wsRefreshTimerRef.current =
            null;
        }
  
        socket.emit(
          "unsubscribe:companyprofile"
        );
  
        if (
          companyDetailsId
        ) {
          socket.emit(
            "unsubscribe:companydetails",
            {
              id:
                companyDetailsId,
            }
          );
        }
  
        socket.off(
          "connect",
          subscribe
        );
  
        socket.off(
          "reconnect",
          subscribe
        );
  
        socket.off(
          "company:updated",
          scheduleRefresh
        );
  
        socket.off(
          "company:created",
          scheduleRefresh
        );
  
        socket.off(
          "companydetails:updated",
          scheduleRefresh
        );
  
        socket.off(
          "companydetails:created",
          scheduleRefresh
        );
  
        socket.off(
          "companydetails:deleted",
          scheduleRefresh
        );
  
        socket.off(
          "companyprofile:changed",
          scheduleRefresh
        );
      };
    }, [
      companyDetailsId,
      refresh,
    ]);

  useEffect(() => {
    const sectionId =
      location.hash.replace(
        "#",
        ""
      );

    if (!sectionId) {
      return undefined;
    }

    const timer =
      window.setTimeout(
        () => {
          const element =
            document.getElementById(
              sectionId
            );

          element?.scrollIntoView({
            behavior:
              "smooth",

            block:
              "start",
          });
        },
        150
      );

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    location.hash,
  ]);

  const profileCompletion =
    useMemo(() => {
      const essentialFields = [
        overview.name,
        overview.address,
        overview.city,
        overview.country,
        overview.email,
        overview.phoneNumber,
        overview.taxId,
        overview.currency,
        overview.paymentNetDays,
        overview.driversEmployed,
      ];

      const completed =
        essentialFields.filter(
          hasOverviewValue
        ).length;

      const total =
        essentialFields.length;

      return {
        completed,
        total,

        percentage:
          Math.round(
            (completed /
              total) *
              100
          ),
      };
    }, [
      overview,
    ]);

  const fallbackValue =
    t(
      "overview.not_available"
    );

  const paymentDaysValue =
    overview.paymentNetDays !==
    null
      ? t(
          "overview.summary.payment_days",
          {
            count:
              Number(
                overview.paymentNetDays
              ),
          }
        )
      : fallbackValue;

  const metrics = [
    {
      key:
        "country",

      icon:
        LuGlobe,

      tone:
        "blue",

      label:
        t(
          "overview.summary.country"
        ),

      value:
        overview.country ||
        fallbackValue,

      caption:
        "País da empresa",
    },

    {
      key:
        "city",

      icon:
        LuMapPin,

      tone:
        "green",

      label:
        t(
          "overview.summary.city"
        ),

      value:
        overview.city ||
        fallbackValue,

      caption:
        "Localização principal",
    },

    {
      key:
        "tax",

      icon:
        LuFileText,

      tone:
        "purple",

      label:
        t(
          "overview.summary.tax_id"
        ),

      value:
        overview.taxId ||
        fallbackValue,

      caption:
        "Identificação fiscal",
    },

    {
      key:
        "currency",

      icon:
        LuCoins,

      tone:
        "orange",

      label:
        t(
          "overview.summary.currency"
        ),

      value:
        overview.currency ||
        fallbackValue,

      caption:
        "Moeda de faturação",
    },

    {
      key:
        "payment",

      icon:
        LuHandshake,

      tone:
        "cyan",

      label:
        t(
          "overview.summary.payment_terms"
        ),

      value:
        paymentDaysValue,

      caption:
        "Prazo acordado",
    },

    {
      key:
        "drivers",

      icon:
        LuTruck,

      tone:
        "red",

      label:
        t(
          "overview.summary.drivers"
        ),

      value:
        overview.driversEmployed !==
        null
          ? overview.driversEmployed
          : fallbackValue,

      caption:
        "Motoristas nos quadros",
    },
  ];

  const refreshOverview =
    async (
      freshData,
      {
        skipRefresh = false,
      } = {}
    ) => {
      if (
        skipRefresh
      ) {
        return freshData;
      }

      return refresh();
    };

  const scrollToSection =
    (
      sectionId
    ) => {
      document
        .getElementById(
          sectionId
        )
        ?.scrollIntoView({
          behavior:
            "smooth",

          block:
            "start",
        });
    };

  const openCompanyPhotoEditor =
    () => {
      if (
        companyProfileRef.current
          ?.openPhotoEditor
      ) {
        companyProfileRef.current
          .openPhotoEditor();

        return;
      }

      scrollToSection(
        "company-profile"
      );
    };

  const locationLabel = [
    overview.city,
    overview.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="company-settings-page">
      <div className="company-settings-shell">
        <section className="company-settings-hero">
          <div className="company-settings-hero__overlay" />

          <div className="company-settings-hero__content">
            <div className="company-settings-hero__main">
              <div className="company-settings-hero__avatarWrap">
                <div
                  className="company-settings-hero__avatar"
                  role="button"
                  tabIndex={0}
                  aria-label="Ver ou ajustar logótipo da empresa"
                  title="Ver ou ajustar logótipo"
                  onClick={
                    openCompanyPhotoEditor
                  }
                  onKeyDown={(
                    event
                  ) => {
                    if (
                      event.key ===
                        "Enter" ||
                      event.key ===
                        " "
                    ) {
                      event.preventDefault();

                      openCompanyPhotoEditor();
                    }
                  }}
                >
                  <span>
                    {getInitials(
                      overview.name
                    )}
                  </span>

                  {overview.photo && (
                    <img
                      crossOrigin="anonymous"
                      decoding="sync"
                      fetchPriority="high"
                      src={
                        overview.photo
                      }
                      alt={
                        overview.name ||
                        t(
                          "overview.fallback_name"
                        )
                      }
                      onError={(
                        event
                      ) => {
                        event.currentTarget.style.display =
                          "none";
                      }}
                    />
                  )}
                </div>

                <button
                  type="button"
                  className="company-settings-hero__avatarEdit"
                  onClick={
                    openCompanyPhotoEditor
                  }
                  aria-label="Alterar logótipo da empresa"
                  title="Alterar logótipo"
                >
                  <LuPencil />
                </button>
              </div>

              <div className="company-settings-hero__identity">
                <h1>
                  {overview.name ||
                    t(
                      "overview.fallback_name"
                    )}
                </h1>

                <p>
                  LOADEvo · Empresa transportadora
                </p>

                {overview.country && (
                  <div className="company-settings-hero__location">
                    <LuGlobe />

                    {
                      overview.country
                    }
                  </div>
                )}

                <div className="company-settings-hero__chips">
                  {locationLabel && (
                    <span className="company-settings-hero__chip">
                      <LuMapPin />

                      {
                        locationLabel
                      }
                    </span>
                  )}

                  {overview.email && (
                    <span className="company-settings-hero__chip">
                      <LuMail />

                      {
                        overview.email
                      }
                    </span>
                  )}

                  {overview.phoneNumber && (
                    <span className="company-settings-hero__chip">
                      <LuPhone />

                      {
                        overview.phoneNumber
                      }
                    </span>
                  )}
                </div>
              </div>
            </div>

            <aside className="company-settings-hero__summary">
              <div className="company-settings-hero__summaryTop">
                <div className="company-settings-hero__summaryIcon">
                  <LuShieldCheck />
                </div>

                <div>
                  <span>
                    Conclusão do perfil
                  </span>

                  <strong>
                    {
                      profileCompletion.percentage
                    }
                    %
                  </strong>
                </div>
              </div>

              <div
                className="company-settings-hero__progress"
                aria-label={`Perfil ${profileCompletion.percentage}% completo`}
              >
                <span
                  style={{
                    width:
                      `${profileCompletion.percentage}%`,
                  }}
                />
              </div>

              <p>
                {
                  profileCompletion.completed
                }{" "}
                de{" "}
                {
                  profileCompletion.total
                }{" "}
                campos essenciais preenchidos
              </p>

              <button
                type="button"
                className="company-settings-hero__editButton"
                onClick={() =>
                  scrollToSection(
                    "company-profile"
                  )
                }
              >
                <LuPencil />

                Editar perfil
              </button>
            </aside>
          </div>
        </section>

        <section className="company-settings-metrics">
          {metrics.map(
            (
              metric
            ) => (
              <ProfileMetric
                key={
                  metric.key
                }
                {...metric}
              />
            )
          )}
        </section>

        <section className="company-settings-grid">
          <ProfilePanel
            id="company-profile"
            icon={
              LuBuilding2
            }
            title={t(
              "tiles.company_profile.title"
            )}
            description={t(
              "tiles.company_profile.desc"
            )}
            className="company-settings-panel--full"
          >
            <div className="company-settings-embedded company-settings-embedded--profile">
              <CompanyProfile
                ref={
                  companyProfileRef
                }
                embedded
                onSaved={
                  refreshOverview
                }
              />
            </div>
          </ProfilePanel>

          <ProfilePanel
            id="operation-details"
            icon={
              LuShieldCheck
            }
            title={t(
              "tiles.operation_details.title"
            )}
            description={t(
              "tiles.operation_details.desc"
            )}
            className="company-settings-panel--full"
          >
            <div className="company-settings-embedded company-settings-embedded--operation">
              <OperationDetails
                embedded
                onSaved={
                  refreshOverview
                }
              />
            </div>
          </ProfilePanel>

          <ProfilePanel
            id="contact-information"
            icon={
              LuUser
            }
            title={t(
              "tiles.contact_information.title"
            )}
            description={t(
              "tiles.contact_information.desc"
            )}
          >
            <div className="company-settings-embedded company-settings-embedded--contact">
              <ContactInformation
                embedded
                onSaved={
                  refreshOverview
                }
              />
            </div>
          </ProfilePanel>

          <ProfilePanel
            id="tax-information"
            icon={
              LuKey
            }
            title={t(
              "tiles.tax_information.title"
            )}
            description={t(
              "tiles.tax_information.desc"
            )}
          >
            <div className="company-settings-embedded company-settings-embedded--tax">
              <TaxInformation
                embedded
                onSaved={
                  refreshOverview
                }
              />
            </div>
          </ProfilePanel>

          <ProfilePanel
            id="financial-information"
            icon={
              LuFileText
            }
            title={t(
              "tiles.financial_information.title"
            )}
            description={t(
              "tiles.financial_information.desc"
            )}
            className="company-settings-panel--full"
          >
            <div className="company-settings-embedded company-settings-embedded--financial">
              <FinancialInformation
                embedded
                onSaved={
                  refreshOverview
                }
              />
            </div>
          </ProfilePanel>

          <ProfilePanel
            id="payment-terms"
            icon={
              LuHandshake
            }
            title={t(
              "tiles.payment_terms.title"
            )}
            description={t(
              "tiles.payment_terms.desc"
            )}
            className="company-settings-panel--full"
          >
            <div className="company-settings-embedded company-settings-embedded--payment">
              <PaymentTerms
                embedded
                onSaved={
                  refreshOverview
                }
              />
            </div>
          </ProfilePanel>
        </section>
      </div>
    </div>
  );
}

function ProfileMetric({
  icon: Icon,
  tone,
  label,
  value,
  caption,
}) {
  return (
    <article className="company-settings-metric">
      <div
        className={`company-settings-metric__icon ${tone}`}
      >
        <Icon />
      </div>

      <div className="company-settings-metric__content">
        <span>
          {label}
        </span>

        <strong
          title={
            String(value)
          }
        >
          {value}
        </strong>

        <p>
          {caption}
        </p>
      </div>
    </article>
  );
}

function ProfilePanel({
  id,
  icon: Icon,
  title,
  description,
  children,
  className = "",
}) {
  return (
    <section
      id={id}
      className={`company-settings-panel ${className}`}
    >
      <header className="company-settings-panel__header">
        <div className="company-settings-panel__icon">
          <Icon />
        </div>

        <div className="company-settings-panel__heading">
          <h2>
            {title}
          </h2>

          <p>
            {description}
          </p>
        </div>
      </header>

      <div className="company-settings-panel__body">
        {children}
      </div>
    </section>
  );
}